import React, { useState, useCallback, useEffect } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material';
import { debounce } from 'lodash';
import OutlinedInput from '@mui/material/OutlinedInput';
import { apiNerisService } from '../../functions/apiNerisService';
import useIsVisible from '../../hooks/useIsVisible';

const toIdArrayOrNull = (value) => {
  if (!value) {
    return null;
  }
  return Array.isArray(value) ? value : [value];
};

// for items without enum
const getAutocompleteOptionsByCustomApi = ({
  url,
  searchValue = '',
  extraParams,
  arrayOfIds = null,
}) => {
  return apiNerisService
    .get(url, {
      params: {
        ...extraParams,
        search: searchValue,
        id: arrayOfIds,
      },
    })
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

const findMissingOptions = (options, optionsCache) => {
  const valuesSet = new Set(optionsCache.map((item) => item.value));
  return options.filter((item) => !valuesSet.has(item.value));
};

const toValuePrimitive = (item) =>
  typeof item === 'object' ? item?.value : item;

export const filterSelectedAutocompleteOptions = ({
  value,
  options,
  multiple,
  onChange,
}) => {
  const checkIsValueExists = (v) => v != null && v !== '';

  const checkExistsInOptions = (options) => (val) =>
    // use the loose equality comparisons. "val" can be a string or a number
    options.some((o) => toValuePrimitive(o) == val);

  // for multiply
  if (multiple) {
    if (!(Array.isArray(value) && value.length)) {
      return;
    }

    const valuesPrimitive = value
      .map(toValuePrimitive)
      .filter(checkIsValueExists);

    if (!valuesPrimitive.length) {
      if (value.length) {
        onChange([]);
      }
      return;
    }

    const filtered = valuesPrimitive.filter(checkExistsInOptions(options));
    // if the array has changed - update the external value
    const changed =
      filtered.length !== valuesPrimitive.length ||
      filtered.some((v, i) => v != valuesPrimitive[i]);

    if (changed) {
      onChange(filtered);
    }
  }

  // for single
  if (checkIsValueExists(value) && !checkExistsInOptions(options)(value)) {
    onChange(null);
  }
};

const ApiAutocompleteByEndpoint = ({
  id,
  value,
  onChange,
  endpoint,
  multiple = false,
  disabled = false,
  size = 'small',
  placeholder = 'Start typing to search...',
  sx = {},
  extraParams,
}) => {
  const [selectedOptionsCache, setSelectedOptionsCache] = useState([]);

  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const { isVisible, bindingRefCallback, currentComponentRef } = useIsVisible({
    finishWhenVisible: true,
  });

  const fetchOptions = useCallback(
    debounce(async (searchValue = '', arrayOfIds) => {
      // fetch only when a user sees this selector
      if (!endpoint || !isVisible) {
        return;
      }

      setLoading(true);
      try {
        const response = await getAutocompleteOptionsByCustomApi({
          url: endpoint,
          searchValue,
          extraParams,
          arrayOfIds,
        });

        if (response.status === 200 && response.data?.data) {
          const newOptions = Object.entries(response.data.data).map(
            ([key, value]) => ({
              value: key,
              label: value.name,
            })
          );

          setOptions(newOptions);

          setSelectedOptionsCache((prevState) => [
            ...prevState,
            ...findMissingOptions(newOptions, prevState),
          ]);

          // Clear the selector if something in the form that this selector depends on
          // was changed and the new options don't match
          // skip if newOptions it`s search
          if (
            currentComponentRef.current?.classList &&
            !currentComponentRef.current.classList.contains('Mui-focused')
          ) {
            filterSelectedAutocompleteOptions({
              value,
              options: newOptions,
              multiple,
              onChange,
            });
          }
        } else throw response;
      } catch (error) {
        console.error('Error fetching options:', error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    [endpoint, isVisible, extraParams]
  );

  // init selector options
  useEffect(() => {
    fetchOptions('', toIdArrayOrNull(value));
  }, [fetchOptions]);

  const findOptionByValue = (val) => {
    // use the loose equality comparisons. "val" can be a string or a number
    return selectedOptionsCache.find((option) => option?.value == val);
  };

  return (
    <Autocomplete
      ref={bindingRefCallback}
      id={id}
      multiple={multiple}
      options={options}
      /* 
        mui expects value as an objects array if multiple
       or object if single
       */
      value={multiple ? value || [] : findOptionByValue(value) || null}
      onChange={(_, newValue) => {
        if (Array.isArray(newValue)) {
          // just item if options got numbers instead of obj
          onChange(newValue.map((item) => toValuePrimitive(item)));
        } else {
          onChange(newValue?.value || null);
        }
      }}
      onInputChange={(event, newInputValue, reason) => {
        // only users change allow
        if (!event) {
          return;
        }

        // allow only input changes for fetching
        const isInput =
          event.target &&
          event.target.nodeType === 1 &&
          typeof event.target.tagName === 'string' &&
          event.target.tagName.toLowerCase() === 'input';

        if (!isInput && reason !== 'clear') {
          return;
        }

        fetchOptions(newInputValue);
      }}
      loading={loading}
      disabled={disabled}
      size={size}
      sx={sx}
      getOptionLabel={(option) => {
        if (typeof option === 'string') {
          return findOptionByValue(option)?.label || option;
        }
        return `${option?.label ?? ''}`;
      }}
      isOptionEqualToValue={(option, value) => {
        if (!option || !value) {
          return false;
        }

        // use the loose equality comparisons. "value" can be a string or a number
        return toValuePrimitive(option) == value;
      }}
      renderInput={(params) => {
        return (
          <OutlinedInput
            {...params.InputProps}
            inputProps={params.inputProps}
            placeholder={placeholder}
            size={size}
            fullWidth
            variant="outlined"
            endAdornment={
              <>
                {loading && <CircularProgress size={20} />}
                {params.InputProps.endAdornment}
              </>
            }
          />
        );
      }}
      renderOption={(props, option) => {
        const label = option?.label || option;

        return (
          <Box
            component="li"
            {...props}
            sx={{ padding: '8px 16px !important' }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {label}
            </Typography>
          </Box>
        );
      }}
      renderTags={(value, getTagProps) => {
        return value.map((item, index) => {
          const option = findOptionByValue(item) || item;
          const { key, ...tagProps } = getTagProps({ index });

          const label = option?.label || option;
          return <Chip key={key} label={label} size="small" {...tagProps} />;
        });
      }}
    />
  );
};

export default ApiAutocompleteByEndpoint;
